import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { HistoryResponse } from "@/components/HistoryDrawer";
import { api } from "@/lib/api/client";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import i18n from "@/lib/i18n/i18n";
import { unwrap } from "@/lib/api/unwrap";
import { queryKeys } from "@/lib/query/keys";

import type {
  Consent,
  InviteMemberBody,
  InviteMemberResult,
  MyInvitation,
  PartnerConsentStatus,
  PartnerGroup,
  PartnerGroupCreate,
  PartnerGroupMembership,
  PartnerInvitation,
  PatchedConsent,
  PatchedLocalPartner,
  PatchedPartnerGroup,
  PatchedSuperUser,
  LocalPartner,
  SuperUser,
  SuperUserInviteBody,
} from "./types";
import type { BackfillStatus, PromoCode } from "./types";

/**
 * Query-key factory for the partners + consents slice. Each collection exposes
 * a stable root so a mutation can invalidate its whole subtree after a write.
 */
export const partnerKeys = {
  all: ["partners"] as const,
  groups: () => [...partnerKeys.all, "groups"] as const,
  group: (id: string) => [...partnerKeys.all, "group", id] as const,
  members: (id: string) => [...partnerKeys.all, "group", id, "members"] as const,
  superUsers: () => [...partnerKeys.all, "super-users"] as const,
  localPartners: () => [...partnerKeys.all, "local-partners"] as const,
  invitations: () => [...partnerKeys.all, "invitations"] as const,
  groupInvitations: (id: string) =>
    [...partnerKeys.all, "invitations", "group", id] as const,
  myInvitations: () => [...partnerKeys.all, "my-invitations"] as const,
  invitationEmail: (groupId: string, kind: string) =>
    [...partnerKeys.all, "group", groupId, "invitation-email", kind] as const,
  promoCodes: (params: Record<string, string>) =>
    [...partnerKeys.all, "promo-codes", params] as const,
  consents: {
    all: ["consents"] as const,
    me: () => ["consents", "me"] as const,
    partner: (profileId: string) =>
      ["consents", "partner", profileId] as const,
  },
};

// --- partner groups ----------------------------------------------------------

export function useGroupsQuery() {
  return useQuery({
    queryKey: partnerKeys.groups(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/partners/groups/"));
      return data.results as PartnerGroup[];
    },
  });
}

export function useGroupQuery(id: string) {
  return useQuery({
    queryKey: partnerKeys.group(id),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/partners/groups/{id}/", {
          params: { path: { id } },
        }),
      ),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PartnerGroupCreate) =>
      unwrap(api.POST("/api/v1/partners/groups/", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: partnerKeys.groups() }),
  });
}

/** Persist a (partial) group edit. Honours the autosave abort signal. */
export function saveGroup(
  id: string,
  body: PatchedPartnerGroup,
  signal?: AbortSignal,
): Promise<PartnerGroup> {
  return unwrap(
    api.PATCH("/api/v1/partners/groups/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  );
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/partners/groups/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: partnerKeys.groups() }),
  });
}

// --- group members + license pool -------------------------------------------

export function useMembersQuery(groupId: string) {
  return useQuery({
    queryKey: partnerKeys.members(groupId),
    enabled: Boolean(groupId),
    queryFn: async () =>
      // spectacular types this action as returning a PartnerGroup; the view
      // returns the membership array (many=True). Cast to the true shape.
      (await unwrap(
        api.GET("/api/v1/partners/groups/{id}/members/", {
          params: { path: { id: groupId } },
        }),
      )) as unknown as PartnerGroupMembership[],
  });
}

/** Invalidate both the members list and the group (license/member counts). */
function useGroupMutation<TArgs, TData = unknown>(
  groupId: string,
  fn: (args: TArgs) => Promise<TData>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partnerKeys.members(groupId) });
      void qc.invalidateQueries({ queryKey: partnerKeys.groupInvitations(groupId) });
      void qc.invalidateQueries({ queryKey: partnerKeys.group(groupId) });
      void qc.invalidateQueries({ queryKey: partnerKeys.groups() });
    },
  });
}

// These actions read no request body (spectacular declares an optional
// PartnerGroup body; the view ignores it), so we omit it.
export function useGrantLicense(groupId: string) {
  return useGroupMutation(groupId, (userId: string) =>
    unwrap(
      api.POST("/api/v1/partners/groups/{id}/grant-license/{user_id}/", {
        params: { path: { id: groupId, user_id: userId } },
      }),
    ),
  );
}

export function useRevokeLicense(groupId: string) {
  return useGroupMutation(groupId, (userId: string) =>
    unwrap(
      api.POST("/api/v1/partners/groups/{id}/revoke-license/{user_id}/", {
        params: { path: { id: groupId, user_id: userId } },
      }),
    ),
  );
}

export function useRevokeMember(groupId: string) {
  return useGroupMutation(groupId, (userId: string) =>
    unwrap(
      api.POST("/api/v1/partners/groups/{id}/revoke-member/{user_id}/", {
        params: { path: { id: groupId, user_id: userId } },
      }),
    ),
  );
}

// --- invitations -------------------------------------------------------------

export function useInviteMember(groupId: string) {
  return useGroupMutation(groupId, (body: InviteMemberBody) =>
    // The action body is typed as PartnerGroup by spectacular; send the true
    // invite payload and cast the union result (invitation / confirmation —
    // see InviteMemberResult).
    unwrap(
      api.POST("/api/v1/partners/groups/{id}/invite-member/", {
        params: { path: { id: groupId } },
        body: body as never,
      }),
    ) as unknown as Promise<InviteMemberResult>,
  );
}

/** The invitation kinds whose body a group can personalize. */
export type InvitationEmailKind = "member" | "local_partner";

export interface InvitationEmailBody {
  html: string;
  is_custom: boolean;
  default: string;
}

/** The body this group would send for one invitation kind.
 *
 * `html` is the group's personalized body, or the packaged default when it has
 * none — `is_custom` says which. The endpoint is an untyped `@action`, so the
 * result is cast rather than inferred.
 */
export function useInvitationEmail(
  groupId: string | null,
  kind: InvitationEmailKind,
) {
  return useQuery({
    queryKey: partnerKeys.invitationEmail(groupId ?? "", kind),
    enabled: Boolean(groupId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/partners/groups/{id}/invitation-email/{kind}/", {
          params: { path: { id: groupId as string, kind } },
        }),
      ) as unknown as Promise<InvitationEmailBody>,
  });
}

/** Persist a personalized body. The server sanitizes and echoes what it stored. */
export function useSaveInvitationEmail(
  groupId: string,
  kind: InvitationEmailKind,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (html: string) =>
      unwrap(
        api.POST("/api/v1/partners/groups/{id}/invitation-email/{kind}/", {
          params: { path: { id: groupId, kind } },
          body: { html } as never,
        }),
      ) as unknown as Promise<{ html: string }>,
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: partnerKeys.invitationEmail(groupId, kind),
      }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(
        api.POST("/api/v1/partners/invitations/{id}/resend/", {
          params: { path: { id: invitationId } },
        }),
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: partnerKeys.invitations() }),
  });
}

export function useCancelInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(
        api.POST("/api/v1/partners/invitations/{id}/cancel/", {
          params: { path: { id: invitationId } },
        }),
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: partnerKeys.invitations() }),
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      unwrap(
        api.POST("/api/v1/partners/invitations/{token}/accept/", {
          params: { path: { token } },
        }),
      ),
    // Membership/role may have changed: refresh the caller's group lists and
    // the session, whose role gates the groups page a new local partner goes
    // to. Returned, so the mutation settles once both are fresh.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: partnerKeys.all }),
        qc.invalidateQueries({ queryKey: queryKeys.auth.me() }),
      ]),
  });
}

/** Validation result for a partner invitation token (public, pre-signup). */
export interface PartnerInvitationValidation {
  status:
    | "valid"
    | "expired"
    | "used"
    | "declined"
    | "licence_cap"
    | "invalid"
    // The address has an account by now: answer after logging in (a group's
    // invitation), or ask the CQDD (an administrator's).
    | "account_exists"
    | "account_exists_cqdd";
  email: string | null;
}

/** Validate an invitation token before signup (public) — drives the landing
 * route's message + signup email prefill (A-5). */
export function usePartnerInvitationValidate(token: string) {
  return useQuery({
    queryKey: [...partnerKeys.all, "invitation-validate", token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/partners/invitations/{token}/validate/", {
          params: { path: { token } },
        }),
      )) as unknown as PartnerInvitationValidation,
  });
}

// --- super users (partenaires régionaux) — admin only -----------------------

export function useSuperUsersQuery() {
  return useQuery({
    queryKey: partnerKeys.superUsers(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/partners/super-users/"));
      return data.results as SuperUser[];
    },
  });
}

export function useInviteSuperUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SuperUserInviteBody) =>
      unwrap(
        api.POST("/api/v1/partners/super-users/invite/", {
          body: body as never,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partnerKeys.invitations() });
      void qc.invalidateQueries({ queryKey: partnerKeys.superUsers() });
    },
  });
}

/** Invite (or promote) a local partner into a group — admin provisioning. */
export function useInviteLocalPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SuperUserInviteBody & { partner_group?: string }) =>
      unwrap(
        api.POST("/api/v1/partners/local-partners/invite/", {
          body: body as never,
        }),
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: partnerKeys.invitations() }),
  });
}

/** Still-pending regional + local-partner invitations (admin resend/cancel). */
export function usePendingInvitations() {
  return useQuery({
    queryKey: partnerKeys.invitations(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/partners/invitations/"));
      return data.results as PartnerInvitation[];
    },
  });
}

/** Persist a (partial) super-user pool edit. Honours the autosave abort signal. */
export function saveSuperUser(
  id: string,
  body: PatchedSuperUser,
  signal?: AbortSignal,
): Promise<SuperUser> {
  return unwrap(
    api.PATCH("/api/v1/partners/super-users/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  );
}

// --- local partners (partenaires locaux) — admin only -----------------------

export function useLocalPartnersQuery() {
  return useQuery({
    queryKey: partnerKeys.localPartners(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/partners/local-partners/"));
      return data.results as LocalPartner[];
    },
  });
}

/** Persist a (partial) local-partner pool/discount edit (autosave-aware). */
export function saveLocalPartner(
  id: string,
  body: PatchedLocalPartner,
  signal?: AbortSignal,
): Promise<LocalPartner> {
  return unwrap(
    api.PATCH("/api/v1/partners/local-partners/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  );
}

// --- consents ----------------------------------------------------------------

/** The member's own consent flags on their enterprise profile. */
export function useMyConsentQuery(enabled = true) {
  return useQuery({
    queryKey: partnerKeys.consents.me(),
    enabled,
    retry: false,
    queryFn: () => unwrap(api.GET("/api/v1/consents/me/")),
  });
}

/** Persist the member's consent flags. Honours the autosave abort signal. */
export function saveMyConsent(
  body: PatchedConsent,
  signal?: AbortSignal,
): Promise<Consent> {
  return unwrap(api.PATCH("/api/v1/consents/me/", { body, signal }));
}

/** Partner view of a member's consent status, by enterprise profile id. */
export function usePartnerConsentQuery(profileId: string) {
  return useQuery({
    queryKey: partnerKeys.consents.partner(profileId),
    enabled: Boolean(profileId),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/consents/{enterprise_profile_id}/", {
          params: { path: { enterprise_profile_id: profileId } },
        }),
      )) as unknown as PartnerConsentStatus,
  });
}


/* ------------------------------------------------------------------ promo codes */

/**
 * Active Stripe promotion codes and whether a partner group claims them.
 *
 * Filtering happens server-side over the Stripe response — there is no local
 * table of promotion codes — so the params go straight through rather than
 * being applied here.
 */
export function usePromoCodesQuery(params: Record<string, string>) {
  return useQuery({
    queryKey: partnerKeys.promoCodes(params),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/partners/promo-codes/", {
          params: { query: params },
        }),
      ) as Promise<PromoCode[]>,
  });
}

/**
 * Every partner group, for the bind picker.
 *
 * Not `useGroupsQuery`: that endpoint answers "my groups" for everyone, admins
 * included, so an admin binding a code would only see groups they own.
 */
export function usePromoCodeGroupsQuery() {
  return useQuery({
    queryKey: [...partnerKeys.all, "promo-code-groups"],
    queryFn: () =>
      unwrap(api.GET("/api/v1/partners/promo-codes/groups/")) as Promise<
        { id: string; name: string }[]
      >,
  });
}

export function useBindPromoCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; partner_group: string }) =>
      unwrap(
        api.POST("/api/v1/partners/promo-codes/bind/", { body: body as never }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

/**
 * Start a backfill scan. Returns a token to poll — the scan reads Stripe
 * invoice history in a task, so it cannot answer inside the request.
 */
export function useStartBackfill() {
  return useMutation({
    mutationFn: (groupId: string) =>
      unwrap(
        api.POST("/api/v1/partners/promo-codes/backfill/{group_id}/", {
          params: { path: { group_id: groupId } },
        }),
      ) as Promise<{ token: string; group_name: string }>,
  });
}

/**
 * Poll a running scan. `enabled` gates it so nothing polls until a scan starts,
 * and `refetchInterval` stops once the payload is ready.
 */
export function useBackfillStatus(token: string | null) {
  return useQuery({
    queryKey: [...partnerKeys.all, "backfill", token],
    enabled: token != null,
    refetchInterval: (query) =>
      (query.state.data as BackfillStatus | undefined)?.state === "ready"
        ? false
        : 2000,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/partners/promo-codes/backfill/status/{token}/", {
          params: { path: { token: token as string } },
        }),
      ) as Promise<BackfillStatus>,
  });
}

export function useConfirmBackfill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; emails: string[] }) =>
      unwrap(
        api.POST("/api/v1/partners/promo-codes/backfill/confirm/", {
          body: body as never,
        }),
      ) as Promise<{ joined: number; over_cap: number; failed: { email: string; reason: string }[] }>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

/**
 * One group's modification history for the drawer.
 *
 * A plain fetch rather than the typed client: the history action is untyped in
 * the schema, the same reason the tools drawer does it this way. It hangs off
 * the group viewset, so the server scopes it to groups the caller may see.
 */
export function useGroupHistory(groupId: string | null) {
  return useQuery({
    // Language is part of the key: the server renders the diff labels per
    // Accept-Language, so switching language must refetch rather than serve a
    // stale-locale cache.
    queryKey: [...partnerKeys.all, "group-history", groupId, i18n.language],
    enabled: groupId != null,
    queryFn: async (): Promise<HistoryResponse> => {
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (i18n.language) headers["Accept-Language"] = i18n.language;
      const res = await fetch(
        `${API_ORIGIN}/api/v1/partners/groups/${groupId}/history/`,
        { headers },
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as HistoryResponse;
    },
  });
}

/** The signed-in account's own live member invitations — one dashboard notice
 * each (mirrors the notices on the Django home page). */
export function useMyInvitations() {
  return useQuery({
    queryKey: partnerKeys.myInvitations(),
    queryFn: async () =>
      (await unwrap(api.GET("/api/v1/partners/my-invitations/", {}))) as MyInvitation[],
  });
}

/** Accept one of the caller's own invitations. Joining changes the account's
 * groups and consent requirements, so the session user is refreshed too. */
export function useAcceptMyInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/v1/partners/my-invitations/{id}/accept/", {
          params: { path: { id } },
        }),
      ),
    // Returned, so the caller's own onSuccess runs once the session user is
    // fresh: a new local partner navigates to /partner-group, which is gated
    // on the role the refetch brings.
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: partnerKeys.all }),
        qc.invalidateQueries({ queryKey: queryKeys.auth.me() }),
      ]),
  });
}

/** Refuse one of the caller's own invitations. */
export function useDeclineMyInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/v1/partners/my-invitations/{id}/decline/", {
          params: { path: { id } },
        }),
      ),
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: partnerKeys.myInvitations() }),
  });
}

/** A group's unaccepted invitations — pending, expired, refused — newest first.
 * Lives under `partnerKeys.invitations()`, so invite/resend/cancel refresh it. */
export function useGroupInvitations(groupId: string) {
  return useQuery({
    queryKey: partnerKeys.groupInvitations(groupId),
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/partners/groups/{id}/invitations/", {
          params: { path: { id: groupId } },
        }),
      )) as PartnerInvitation[],
  });
}
